import { useState, useEffect } from 'react';
import api from '../services/api';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

const TYPE_LABEL = { pto: 'PTO', sick: 'Sick Leave', unpaid: 'Unpaid' };

// Format a stored DATE without timezone shift (the API sends UTC-midnight ISO strings).
function fmtDate(d) {
  if (!d) return '';
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString();
}
const STATUS_COLORS = {
  pending: 'bg-yellow-900 text-yellow-300',
  approved: 'bg-green-900 text-green-300',
  denied: 'bg-red-900 text-red-400',
};

function BalanceBar({ label, used, total }) {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const remaining = Math.max(total - used, 0);
  return (
    <div>
      <div className="flex justify-between text-xs text-gray-400 mb-1">
        <span>{label}</span>
        <span>{remaining} / {total} hrs remaining</span>
      </div>
      <div className="h-2 rounded-full bg-navy-700">
        <div
          className="h-2 rounded-full bg-gold-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function LeavePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [myData, setMyData] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ leave_type: 'pto', start_date: '', end_date: '', days: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [editingBalance, setEditingBalance] = useState(null);
  const [balanceForm, setBalanceForm] = useState({ pto_total: '', sick_total: '' });
  const [adminForm, setAdminForm] = useState({ user_id: '', leave_type: 'pto', start_date: '', end_date: '', days: '', note: '' });
  const [adminSubmitting, setAdminSubmitting] = useState(false);
  const [adminError, setAdminError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const myRes = await api.get('/leave/my');
      setMyData(myRes.data);
      if (isAdmin) {
        const adminRes = await api.get('/leave/admin');
        setAdminData(adminRes.data);
      }
    } catch (err) {
      console.error('Leave fetch error:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.start_date || !form.end_date || !form.days) {
      setError('Please fill in all required fields');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await api.post('/leave/request', form);
      setShowForm(false);
      setForm({ leave_type: 'pto', start_date: '', end_date: '', days: '', note: '' });
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReview(requestId, status) {
    try {
      await api.patch(`/leave/request/${requestId}`, { status });
      await fetchData();
    } catch {
      alert('Failed to update request');
    }
  }

  async function handleDeleteRequest(id) {
    if (!window.confirm('Delete this request permanently? If it was approved, the hours are returned to the balance.')) return;
    try {
      await api.delete(`/leave/request/${id}`);
      await fetchData();
    } catch {
      alert('Failed to delete request');
    }
  }

  async function handleAdminCreate(e) {
    e.preventDefault();
    if (!adminForm.user_id || !adminForm.start_date || !adminForm.end_date || !adminForm.days) {
      setAdminError('Pick a person and fill in the dates and hours.');
      return;
    }
    setAdminError('');
    setAdminSubmitting(true);
    try {
      await api.post('/leave/admin/request', adminForm);
      setAdminForm({ user_id: '', leave_type: 'pto', start_date: '', end_date: '', days: '', note: '' });
      await fetchData();
    } catch (err) {
      setAdminError(err.response?.data?.error || 'Failed to add time off');
    } finally {
      setAdminSubmitting(false);
    }
  }

  async function handleBalanceSave(userId) {
    try {
      await api.patch(`/leave/balance/${userId}`, {
        pto_total: parseFloat(balanceForm.pto_total) || 0,
        sick_total: parseFloat(balanceForm.sick_total) || 0,
      });
      setEditingBalance(null);
      await fetchData();
    } catch {
      alert('Failed to update balance');
    }
  }

  if (loading) return <Layout><p className="text-gray-500 text-sm">Loading…</p></Layout>;

  const balance = myData?.balance;
  const hr = myData?.hr; // HR's authoritative balance (null if not in HR / integration off)
  const myRequests = myData?.requests || [];

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8">

        {/* My Balances — HR is the master tracker; fall back to local if not linked */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-white">Time Off</h1>
            {hr && <span className="text-[11px] uppercase tracking-wider text-green-400">● Synced with HR</span>}
          </div>
          <div className="rounded-xl border border-navy-500 p-5 space-y-4" style={{ backgroundColor: '#142236' }}>
            {hr ? (
              hr.pto_tier === 'flex' ? (
                <div className="text-center py-2">
                  <div className="text-gold-400 font-bold uppercase tracking-wide">Unlimited PTO</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {hr.pto_used || 0} hrs taken this year
                    {hr.pending_hours ? ` · ${hr.pending_hours} pending` : ''}
                  </div>
                </div>
              ) : (
                <>
                  <BalanceBar label="PTO" used={parseFloat(hr.pto_used || 0)} total={parseFloat(hr.pto_annual_hours || 0)} />
                  <BalanceBar label="Sick Leave" used={parseFloat(hr.sick_used || 0)} total={parseFloat(hr.sick_accrued || 0)} />
                  {hr.pending_hours > 0 && (
                    <div className="text-[11px] text-yellow-300/80">{hr.pending_hours} hrs pending approval</div>
                  )}
                </>
              )
            ) : (
              <>
                <BalanceBar label="PTO" used={parseFloat(balance?.pto_used || 0)} total={parseFloat(balance?.pto_total || 0)} />
                <BalanceBar label="Sick Leave" used={parseFloat(balance?.sick_used || 0)} total={parseFloat(balance?.sick_total || 0)} />
                <div className="text-[11px] text-gray-500">Local balance — not yet linked to HR.</div>
              </>
            )}
          </div>
        </div>

        {/* Request Form */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white font-bold">My Requests</h2>
            <button
              onClick={() => setShowForm((v) => !v)}
              className="bg-gold-500 text-navy-900 text-sm px-4 py-2 rounded font-bold uppercase tracking-wide hover:bg-gold-400 transition"
            >
              {showForm ? 'Cancel' : '+ New Request'}
            </button>
          </div>

          {showForm && (
            <form onSubmit={handleSubmit} className="rounded-xl border border-navy-500 p-5 mb-4 space-y-4" style={{ backgroundColor: '#142236' }}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wide block mb-1">Type</label>
                  <select
                    value={form.leave_type}
                    onChange={(e) => setForm({ ...form, leave_type: e.target.value })}
                    className="w-full rounded border border-navy-500 px-3 py-2 text-sm text-white focus:outline-none focus:border-gold-500"
                    style={{ backgroundColor: '#0f1a29' }}
                  >
                    <option value="pto">PTO</option>
                    <option value="sick">Sick Leave</option>
                    <option value="unpaid">Unpaid</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wide block mb-1">Hours</label>
                  <input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={form.days}
                    onChange={(e) => setForm({ ...form, days: e.target.value })}
                    placeholder="e.g. 8 (full day) or 4 (half)"
                    className="w-full rounded border border-navy-500 px-3 py-2 text-sm text-white focus:outline-none focus:border-gold-500"
                    style={{ backgroundColor: '#0f1a29' }}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wide block mb-1">Start Date</label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="w-full rounded border border-navy-500 px-3 py-2 text-sm text-white focus:outline-none focus:border-gold-500"
                    style={{ backgroundColor: '#0f1a29' }}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wide block mb-1">End Date</label>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="w-full rounded border border-navy-500 px-3 py-2 text-sm text-white focus:outline-none focus:border-gold-500"
                    style={{ backgroundColor: '#0f1a29' }}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide block mb-1">Note (optional)</label>
                <textarea
                  rows={2}
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="w-full rounded border border-navy-500 px-3 py-2 text-sm text-white focus:outline-none focus:border-gold-500 resize-none"
                  style={{ backgroundColor: '#0f1a29' }}
                />
              </div>
              {error && <p className="text-red-400 text-xs">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="bg-gold-500 text-navy-900 px-6 py-2 rounded font-bold uppercase tracking-wide text-sm hover:bg-gold-400 disabled:opacity-50 transition"
              >
                {submitting ? 'Submitting…' : 'Submit Request'}
              </button>
            </form>
          )}

          {/* My request history */}
          {myRequests.length === 0 ? (
            <p className="text-gray-500 text-sm">No requests yet.</p>
          ) : (
            <div className="rounded-xl border border-navy-500 overflow-hidden" style={{ backgroundColor: '#142236' }}>
              <table className="w-full text-sm">
                <thead className="border-b border-navy-500" style={{ backgroundColor: '#0f1a29' }}>
                  <tr>
                    {['Type', 'Dates', 'Hours', 'Note', 'Status'].map((h) => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy-600">
                  {myRequests.map((r) => (
                    <tr key={r.id} className="hover:bg-navy-600 transition">
                      <td className="px-4 py-3 text-white font-medium">{TYPE_LABEL[r.leave_type]}</td>
                      <td className="px-4 py-3 text-gray-300 text-xs">
                        {fmtDate(r.start_date)} – {fmtDate(r.end_date)}
                      </td>
                      <td className="px-4 py-3 text-gray-300">{r.days}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs max-w-xs truncate">{r.note || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${STATUS_COLORS[r.status]}`}>
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Admin Section */}
        {isAdmin && adminData && (
          <>
            {/* Add time off on behalf of someone */}
            <div className="rounded-xl border border-navy-500 p-5" style={{ backgroundColor: '#142236' }}>
              <h2 className="text-white font-bold mb-1">Add Time Off (on behalf of)</h2>
              <p className="text-gray-500 text-xs mb-4">Logs an approved time-off event for a team member. PTO/sick deduct from their balance; unpaid does not.</p>
              <form onSubmit={handleAdminCreate} className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
                <div className="col-span-2 md:col-span-2">
                  <label className="block text-xs text-gray-400 mb-1">Person</label>
                  <select
                    value={adminForm.user_id}
                    onChange={(e) => setAdminForm({ ...adminForm, user_id: e.target.value })}
                    className="w-full rounded px-3 py-2 text-sm border focus:outline-none"
                    style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f', color: '#c9a84c' }}
                  >
                    <option value="">Select…</option>
                    {adminData.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Type</label>
                  <select
                    value={adminForm.leave_type}
                    onChange={(e) => setAdminForm({ ...adminForm, leave_type: e.target.value })}
                    className="w-full rounded px-3 py-2 text-sm border focus:outline-none text-white"
                    style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}
                  >
                    <option value="pto">PTO</option>
                    <option value="sick">Sick</option>
                    <option value="unpaid">Unpaid</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Start</label>
                  <input type="date" value={adminForm.start_date}
                    onChange={(e) => setAdminForm({ ...adminForm, start_date: e.target.value })}
                    className="w-full rounded px-3 py-2 text-sm border focus:outline-none text-white"
                    style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }} />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">End</label>
                  <input type="date" value={adminForm.end_date}
                    onChange={(e) => setAdminForm({ ...adminForm, end_date: e.target.value })}
                    className="w-full rounded px-3 py-2 text-sm border focus:outline-none text-white"
                    style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }} />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Hours</label>
                  <input type="number" step="0.5" min="0.5" value={adminForm.days}
                    onChange={(e) => setAdminForm({ ...adminForm, days: e.target.value })}
                    className="w-full rounded px-3 py-2 text-sm border focus:outline-none text-white"
                    style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }} />
                </div>
                <input
                  placeholder="Note (optional)"
                  value={adminForm.note}
                  onChange={(e) => setAdminForm({ ...adminForm, note: e.target.value })}
                  className="col-span-2 md:col-span-5 rounded px-3 py-2 text-sm border focus:outline-none text-white placeholder-gray-500"
                  style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}
                />
                <button type="submit" disabled={adminSubmitting}
                  className="col-span-2 md:col-span-1 py-2 rounded text-sm font-bold uppercase tracking-wide disabled:opacity-50"
                  style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}>
                  {adminSubmitting ? 'Adding…' : 'Add'}
                </button>
                {adminError && <p className="col-span-2 md:col-span-6 text-red-400 text-xs">{adminError}</p>}
              </form>
            </div>

            {/* Pending requests */}
            <div>
              <h2 className="text-white font-bold mb-4">Pending Requests</h2>
              {adminData.requests.filter((r) => r.status === 'pending').length === 0 ? (
                <p className="text-gray-500 text-sm">No pending requests.</p>
              ) : (
                <div className="rounded-xl border border-navy-500 overflow-hidden" style={{ backgroundColor: '#142236' }}>
                  <table className="w-full text-sm">
                    <thead className="border-b border-navy-500" style={{ backgroundColor: '#0f1a29' }}>
                      <tr>
                        {['Employee', 'Type', 'Dates', 'Hours', 'Note', ''].map((h) => (
                          <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-navy-600">
                      {adminData.requests.filter((r) => r.status === 'pending').map((r) => (
                        <tr key={r.id} className="hover:bg-navy-600 transition">
                          <td className="px-4 py-3 text-white font-medium">{r.user_name}</td>
                          <td className="px-4 py-3 text-gray-300">{TYPE_LABEL[r.leave_type]}</td>
                          <td className="px-4 py-3 text-gray-300 text-xs">
                            {fmtDate(r.start_date)} – {fmtDate(r.end_date)}
                          </td>
                          <td className="px-4 py-3 text-gray-300">{r.days}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs max-w-xs truncate">{r.note || '—'}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleReview(r.id, 'approved')}
                                className="bg-green-700 text-white text-xs px-3 py-1 rounded font-bold hover:bg-green-600 transition"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleReview(r.id, 'denied')}
                                className="bg-red-900 text-red-300 text-xs px-3 py-1 rounded font-bold hover:bg-red-800 transition"
                              >
                                Deny
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Team balances */}
            <div>
              <h2 className="text-white font-bold mb-4">Team Balances — {adminData.year}</h2>
              <div className="rounded-xl border border-navy-500 overflow-hidden" style={{ backgroundColor: '#142236' }}>
                <table className="w-full text-sm">
                  <thead className="border-b border-navy-500" style={{ backgroundColor: '#0f1a29' }}>
                    <tr>
                      {['Employee', 'Role', 'PTO Remaining', 'Sick Remaining', ''].map((h) => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-navy-600">
                    {adminData.users.map((u) => (
                      <tr key={u.id} className="hover:bg-navy-600 transition">
                        <td className="px-4 py-3 text-white font-medium">{u.name}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs uppercase">{u.role}</td>
                        <td className="px-4 py-3 text-gray-300">
                          {Math.max(u.pto_total - u.pto_used, 0)} / {u.pto_total} hrs
                        </td>
                        <td className="px-4 py-3 text-gray-300">
                          {Math.max(u.sick_total - u.sick_used, 0)} / {u.sick_total} hrs
                        </td>
                        <td className="px-4 py-3">
                          {editingBalance === u.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                placeholder="PTO"
                                value={balanceForm.pto_total}
                                onChange={(e) => setBalanceForm({ ...balanceForm, pto_total: e.target.value })}
                                className="w-16 rounded border border-navy-500 px-2 py-1 text-xs text-white focus:outline-none"
                                style={{ backgroundColor: '#0f1a29' }}
                              />
                              <input
                                type="number"
                                placeholder="Sick"
                                value={balanceForm.sick_total}
                                onChange={(e) => setBalanceForm({ ...balanceForm, sick_total: e.target.value })}
                                className="w-16 rounded border border-navy-500 px-2 py-1 text-xs text-white focus:outline-none"
                                style={{ backgroundColor: '#0f1a29' }}
                              />
                              <button
                                onClick={() => handleBalanceSave(u.id)}
                                className="text-xs bg-gold-500 text-navy-900 px-2 py-1 rounded font-bold hover:bg-gold-400 transition"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingBalance(null)}
                                className="text-xs text-gray-500 hover:text-white transition"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setEditingBalance(u.id);
                                setBalanceForm({ pto_total: u.pto_total, sick_total: u.sick_total });
                              }}
                              className="text-xs text-gold-500 hover:text-gold-400 transition"
                            >
                              Edit Balance
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* All request history */}
            <div>
              <h2 className="text-white font-bold mb-4">All Requests</h2>
              <div className="rounded-xl border border-navy-500 overflow-hidden" style={{ backgroundColor: '#142236' }}>
                <table className="w-full text-sm">
                  <thead className="border-b border-navy-500" style={{ backgroundColor: '#0f1a29' }}>
                    <tr>
                      {['Employee', 'Type', 'Dates', 'Hours', 'Status', ''].map((h) => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-navy-600">
                    {adminData.requests.map((r) => (
                      <tr key={r.id} className="hover:bg-navy-600 transition">
                        <td className="px-4 py-3 text-white font-medium">{r.user_name}</td>
                        <td className="px-4 py-3 text-gray-300">{TYPE_LABEL[r.leave_type]}</td>
                        <td className="px-4 py-3 text-gray-300 text-xs">
                          {fmtDate(r.start_date)} – {fmtDate(r.end_date)}
                        </td>
                        <td className="px-4 py-3 text-gray-300">{r.days}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${STATUS_COLORS[r.status]}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {r.status === 'approved' && (
                              <button
                                onClick={() => handleReview(r.id, 'denied')}
                                className="text-xs text-red-400 hover:text-red-300 transition"
                              >
                                Revoke
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteRequest(r.id)}
                              className="text-xs text-gray-500 hover:text-red-400 transition"
                              title="Delete request"
                            >
                              ✕
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
